import { setTokenHeader } from 'librechat-data-provider';

/** Roles permitted to use this dashboard. A coachee account has no business here. */
const ALLOWED_ROLES = new Set(['LEADHR', 'ADMIN']);

const TOKEN_KEY = 'leadhr:token';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role: string;
}

export class AuthError extends Error {
  constructor(
    message: string,
    /** Distinguishes "wrong password" from "this account may not use the dashboard". */
    readonly kind: 'credentials' | 'forbidden' | 'network' = 'credentials',
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

interface LoginResponse {
  token?: string;
  user?: { _id?: string; id?: string; email?: string; name?: string; role?: string };
}

export function canUseDashboard(role: string | undefined): boolean {
  return !!role && ALLOWED_ROLES.has(role);
}

function storeToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* Private browsing blocks storage; the session still works until the tab closes. */
  }
  setTokenHeader(token);
}

export function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearStoredToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
  setTokenHeader(undefined);
}

async function readJson(response: Response): Promise<LoginResponse | null> {
  try {
    return (await response.json()) as LoginResponse;
  } catch {
    return null;
  }
}

function toAuthUser(payload: LoginResponse['user']): AuthUser {
  return {
    id: payload?._id ?? payload?.id ?? '',
    email: payload?.email ?? '',
    name: payload?.name,
    role: payload?.role ?? '',
  };
}

/**
 * Signs in against the platform's own auth endpoint.
 *
 * The dashboard has its own sign-in but not its own accounts: a Lead HR is a user of the
 * platform, they simply never have to open the chat app. Anyone whose role cannot use the
 * dashboard is rejected here *and* by every company endpoint on the server — this check
 * is for a clear message, not for security.
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  let response: Response;
  try {
    response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email: email.trim(), password }),
    });
  } catch {
    throw new AuthError('Could not reach the server. Please try again.', 'network');
  }

  if (response.status === 401 || response.status === 400) {
    throw new AuthError('That email and password do not match an account.', 'credentials');
  }
  if (response.status === 429) {
    throw new AuthError('Too many attempts. Please wait a moment and try again.', 'credentials');
  }
  if (!response.ok) {
    throw new AuthError('Sign-in failed. Please try again.', 'network');
  }

  const body = (await response.json()) as LoginResponse;
  const user = toAuthUser(body.user);
  if (!body.token) {
    throw new AuthError('Sign-in failed. Please try again.', 'network');
  }
  if (!canUseDashboard(user.role)) {
    throw new AuthError(
      'This account does not have access to the company dashboard.',
      'forbidden',
    );
  }

  storeToken(body.token);
  return user;
}

/**
 * Restores a session from the refresh cookie the login set.
 *
 * Without this a page reload would drop the Lead HR back to sign-in even though their
 * session is still valid.
 */
export async function restoreSession(): Promise<AuthUser | null> {
  const stored = readStoredToken();
  if (stored) {
    setTokenHeader(stored);
  }

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    /**
     * A missing refresh cookie answers 200 with the plain-text "Refresh token not
     * provided", so the status alone does not mean a session exists — the body has to
     * parse and carry a token.
     */
    const body = response.ok ? await readJson(response) : null;
    if (!body?.token) {
      clearStoredToken();
      return null;
    }
    const user = toAuthUser(body.user);
    if (!canUseDashboard(user.role)) {
      clearStoredToken();
      return null;
    }
    storeToken(body.token);
    return user;
  } catch {
    clearStoredToken();
    return null;
  }
}

/**
 * Ends the session on the server as well as locally.
 *
 * `/api/auth/logout` is behind `requireJwtAuth`, and the bearer token lives on the shared
 * axios instance — a bare `fetch` does not carry it. Without the header the call 401s, the
 * server never clears the refresh cookie, and the next page load silently signs the user
 * back in: the sign-out appears to work and then undoes itself.
 */
export async function logout(): Promise<void> {
  const token = readStoredToken();
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    /* Clearing locally is what matters; a failed call must not trap the user. */
  }
  clearStoredToken();
}

/**
 * Asks for a reset email.
 *
 * Resolves the same way whether or not the address exists — the server is deliberately
 * vague here and the screen must not undo that by reporting which emails are registered.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/auth/requestPasswordReset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    });
  } catch {
    throw new AuthError('Could not reach the server. Please try again.', 'network');
  }

  if (response.status === 429) {
    throw new AuthError('Too many attempts. Please wait a moment and try again.', 'credentials');
  }
  /** The deployment can switch password reset off entirely (ALLOW_PASSWORD_RESET). */
  if (response.status === 403) {
    throw new AuthError(
      'Password reset is turned off for this deployment. Please contact your administrator.',
      'forbidden',
    );
  }
  /** 400 covers a disallowed email domain, which is a real answer worth surfacing. */
  if (response.status === 400) {
    const body = await readJson(response);
    throw new AuthError(
      (body as { message?: string } | null)?.message ?? 'That email cannot be used here.',
      'credentials',
    );
  }
  if (!response.ok) {
    throw new AuthError('Could not send the reset email. Please try again.', 'network');
  }
}

export interface ResetPasswordParams {
  userId: string;
  token: string;
  password: string;
}

/** Completes a reset using the `userId` and `token` from the emailed link. */
export async function resetPassword({
  userId,
  token,
  password,
}: ResetPasswordParams): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/auth/resetPassword', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, token, password }),
    });
  } catch {
    throw new AuthError('Could not reach the server. Please try again.', 'network');
  }

  if (!response.ok) {
    const body = await readJson(response);
    throw new AuthError(
      (body as { message?: string } | null)?.message ??
        'That reset link is no longer valid. Please request a new one.',
      'credentials',
    );
  }
}
